/* ----------------------------------------------------------------------------------------------------------------------
   VARIANT SELECTION GATE
   ----------------------------------------------------------------------------------------------------------------------

   Makes the variant picker start on a "Choose an option" placeholder. The buy buttons stay fully coloured and
   clickable, but until every option has a value a press on them opens the model dropdown instead of adding to cart.

   The theme's own <variant-picker> element drives variant changes from the radio inputs it renders. This script does
   not replace that: the radios are still there (visually hidden), and the visible <select> simply mirrors its choice
   onto them and lets the theme take it from there. That way price, media, inventory and section re-rendering all keep
   working exactly as before.

   Paired with assets/variant-selection.css and snippets/variant-picker.liquid.
---------------------------------------------------------------------------------------------------------------------- */

(() => {
  const PENDING = 'pending';
  const READY = 'ready';
  const GATE_ATTRIBUTE = 'data-variant-gate';

  /**
   * Every element whose appearance depends on whether a variant has been chosen for a given form.
   */
  const gatedElementsFor = (formId) => {
    const elements = [];
    const escapedId = CSS.escape(formId);
    const form = document.getElementById(formId);
    const productForm = form && form.closest('product-form');

    if (productForm) {
      elements.push(productForm);
    }

    document
      .querySelectorAll(
        `variant-picker[form-id="${escapedId}"], product-sticky-bar[form="${escapedId}"], available-offers[form="${escapedId}"]`
      )
      .forEach((element) => elements.push(element));

    return elements;
  };

  const selectsOf = (picker) => Array.from(picker.querySelectorAll('select[data-variant-select]'));

  const radiosOf = (picker, position) =>
    Array.from(picker.querySelectorAll(`input[type="radio"][data-option-position="${position}"]`));

  const setState = (formId, state) => {
    gatedElementsFor(formId).forEach((element) => element.setAttribute(GATE_ATTRIBUTE, state));
  };

  const formIdFor = (host) => {
    if (host.matches('product-form')) {
      const form = host.querySelector('form');
      return form && form.getAttribute('id');
    }

    return host.getAttribute('form');
  };

  /**
   * Open a native option list without waiting for a second tap. showPicker() needs transient user activation, which a
   * click handler has, but it is only implemented for <select> in newer browsers — hence the guard and the try/catch.
   * Where it is missing (notably older iOS Safari) the scroll and the red flash are the whole cue.
   */
  const openPicker = (select) => {
    try {
      if (typeof select.showPicker === 'function') {
        select.showPicker();
      }
    } catch (error) {
      // Refused by the browser. The customer still has a scrolled-to, focused, highlighted dropdown to tap.
    }
  };

  const nudge = (formId) => {
    if (!formId) return;

    const picker = document.querySelector(`variant-picker[form-id="${CSS.escape(formId)}"]`);
    const emptySelect = picker && selectsOf(picker).find((select) => select.value === '');

    if (!emptySelect) return;

    emptySelect.classList.remove('is-flagged');
    void emptySelect.offsetWidth; // restart the animation if it is already running
    emptySelect.classList.add('is-flagged');

    // Instant, not smooth: the picker opens on the next line, and a dropdown anchored to a still-moving element drifts
    // away from it on desktop.
    emptySelect.scrollIntoView({ block: 'center' });
    emptySelect.focus({ preventScroll: true });
    openPicker(emptySelect);
  };

  /**
   * The chosen option value ids are stashed on the form, which survives the theme's partial re-renders. Without this,
   * a re-rendered picker that comes back from the server without an explicit selection would drop the customer back
   * to the placeholder.
   */
  const rememberChoice = (form, selects) => {
    if (!form) return;

    const values = selects.map((select) => select.value).filter(Boolean);

    if (values.length === selects.length) {
      form.dataset.variantGateChoice = values.join(',');
    }
  };

  const clearSelection = (picker) => {
    selectsOf(picker).forEach((select) => {
      select.value = '';
      radiosOf(picker, select.getAttribute('data-variant-option-position')).forEach((radio) => {
        radio.checked = false;
      });
    });
  };

  /**
   * Re-apply a previously made choice to a freshly rendered picker, without firing a change event (the picker was
   * rendered for this very combination already, so asking the theme to fetch it again would be pointless).
   */
  const restoreChoice = (picker, optionValueIds) => {
    selectsOf(picker).forEach((select) => {
      const match = optionValueIds.find((id) => select.querySelector(`option[value="${CSS.escape(id)}"]`));

      if (!match) return;

      select.value = match;

      radiosOf(picker, select.getAttribute('data-variant-option-position')).forEach((radio) => {
        radio.checked = radio.value === match;
      });
    });
  };

  const init = (picker) => {
    if (picker.variantGateInitialized) return;

    const selects = selectsOf(picker);
    if (selects.length === 0) return;

    picker.variantGateInitialized = true;

    // Set here rather than in the markup: the no-JS fallback hides these selects, and a hidden required control makes
    // the form unsubmittable with no way for the customer to fix it.
    selects.forEach((select) => {
      select.required = true;
    });

    const formId = picker.getAttribute('form-id');
    const form = document.getElementById(formId);
    const storedChoice = form && form.dataset.variantGateChoice;

    if (storedChoice) {
      restoreChoice(picker, storedChoice.split(','));

      // Only trust the restore if it actually filled every option. If anything is still empty, ask again rather than
      // letting the customer buy whatever the server happened to resolve.
      const restored = selects.every((select) => select.value !== '');

      setState(formId, restored ? READY : PENDING);
      return;
    }

    if (picker.getAttribute(GATE_ATTRIBUTE) === PENDING) {
      clearSelection(picker);
      setState(formId, PENDING);
    } else {
      // The page was loaded with an explicit ?variant=, so a real choice already exists.
      rememberChoice(form, selects);
      setState(formId, READY);
    }
  };

  const scan = () => document.querySelectorAll(`variant-picker[${GATE_ATTRIBUTE}]`).forEach(init);

  const onSelectChange = (select) => {
    const picker = select.closest('variant-picker');
    if (!picker) return;

    select.classList.remove('is-flagged');

    const position = select.getAttribute('data-variant-option-position');
    let changedRadio = null;

    radiosOf(picker, position).forEach((radio) => {
      radio.checked = radio.value === select.value;

      if (radio.checked) {
        changedRadio = radio;
      }
    });

    const selects = selectsOf(picker);
    const formId = picker.getAttribute('form-id');
    const complete = selects.every((item) => item.value !== '');

    if (!complete) {
      // Handing a partial combination to the theme would resolve it against the wrong option position, so wait until
      // every option has a value before letting it re-render.
      setState(formId, PENDING);
      return;
    }

    rememberChoice(document.getElementById(formId), selects);
    setState(formId, READY);

    if (changedRadio) {
      // Hand focus back before the theme re-renders the picker. It restores focus to whatever was focused, matched by
      // id, and on mobile focusing a <select> reopens its native option list — which reads as the dropdown popping up
      // a second time straight after the customer already chose.
      select.blur();

      changedRadio.dispatchEvent(new Event('change', { bubbles: true }));
    }
  };

  /**
   * The buy buttons are inert via pointer-events, so the click lands on the <buy-buttons> container. Turn it into a
   * nudge rather than letting it fall on the floor.
   */
  const onClickCapture = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const host = target.closest('product-form, product-sticky-bar, available-offers');
    if (!host || host.getAttribute(GATE_ATTRIBUTE) !== PENDING) return;

    const inOffersPopup = target.closest('.ao-popup__atc');
    if (!inOffersPopup && !target.closest('buy-buttons')) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    // The dropdown lives behind the offers popup, so there is no point pointing at it until the popup is out of the way.
    if (inOffersPopup) {
      host.querySelector('[data-popup-close]')?.click();
    }

    nudge(formIdFor(host));
  };

  /**
   * Last line of defence, for anything that reaches the form without going through a button we know about: keyboard
   * activation, apps, or a theme change that undoes the CSS. Runs in the capture phase so it beats the theme's own
   * <product-form> submit handler.
   */
  const onSubmitCapture = (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const productForm = form.closest('product-form');
    if (!productForm || productForm.getAttribute(GATE_ATTRIBUTE) !== PENDING) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    // Not form.id: the product form holds a hidden input named "id", which shadows the element's own id property.
    nudge(form.getAttribute('id'));
  };

  document.addEventListener('change', (event) => {
    const select = event.target;

    if (select instanceof HTMLSelectElement && select.matches('select[data-variant-select]')) {
      onSelectChange(select);
    }
  });

  document.addEventListener('click', onClickCapture, true);
  document.addEventListener('submit', onSubmitCapture, true);

  // The theme swaps the variant picker out for freshly rendered markup on every variant change, and quick buy drawers
  // inject one on demand, so newly added pickers have to be picked up as they appear. Scanning straight from the
  // callback keeps it ahead of the next paint, which is what stops a re-rendered picker flashing its placeholder.
  // Everything else on the page mutates the DOM too, so filter down to mutations that could actually matter.
  const observer = new MutationObserver((records) => {
    for (const record of records) {
      for (const node of record.addedNodes) {
        if (node.nodeType !== Node.ELEMENT_NODE) continue;

        if (node.matches('variant-picker') || node.querySelector('variant-picker')) {
          scan();
          return;
        }
      }
    }
  });

  const start = () => {
    scan();
    observer.observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();